import { notification } from 'antd';
import type { AxiosError } from 'axios';
import axios from 'axios';

import type { PayloadAction } from '@reduxjs/toolkit';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import type { PgTimezoneName } from '../../../constants/pgTimezoneNames';
import { AppCode, type Org } from '../../../enum';
import { APIStatus } from '../../../enum';
import { removeEmpty } from '../../../helpers/utils.helper';
import type { ErrorMessage } from '../../../models/error';
import type { AsyncThunkConfig } from '../../../models/slice';
import type { FilteredInfo, RequestParams, TableChange } from '../../../models/table';
import { SortedInfo } from '../../../models/table';
import { RaygunErrorHandlerService } from '../../../service/raygun.service';

const { logError } = RaygunErrorHandlerService();

export type Organization = {
  name: string;
  code: Org;
  app_code: AppCode;
  timezone_name: string;
};

export type OrganizationDetails = Organization & {
  label?: string;
  internal: boolean;
  use_service_providers: boolean;
  use_service_providers_coming_soon: boolean;
  use_first_alerts: boolean;
  use_fa_coming_soon: boolean;
  use_dart: boolean;
  access_shared_peers: boolean;
};

export type BQOrganization = {
  name: string | null;
  internal: boolean;
  timezone_name: PgTimezoneName;
};

export const createOrg = (): BQOrganization => ({
  name: null,
  internal: false,
  timezone_name: 'America/Toronto',
});

type OrgsSliceType = {
  orgsForTable: OrganizationDetails[];
  orgsForAdminDic: {
    all: OrganizationDetails[];
    external: OrganizationDetails[];
    ts: OrganizationDetails[];
    bq: OrganizationDetails[];
    sp: OrganizationDetails[];
    fa: OrganizationDetails[];
  };
  orgsForManager: Organization[];
  requestOrgsParams: RequestParams<Organization>;
  orgsApiStatus: APIStatus;
  showAddBQOrganizationModal: boolean;
};

const initialState: OrgsSliceType = {
  orgsForTable: [],
  orgsForAdminDic: {
    all: [],
    external: [],
    ts: [],
    bq: [],
    sp: [],
    fa: [],
  },
  orgsForManager: [],
  requestOrgsParams: {
    pagination: {
      current: 1,
      pageSize: 50,
      defaultPageSize: 50,
    },
    sortedInfo: new SortedInfo<OrganizationDetails>('name', 'ascend'),
    filteredInfo: {},
  },
  orgsApiStatus: APIStatus.IDLE,
  showAddBQOrganizationModal: false,
};

export const fetchOrgsForTable = createAsyncThunk<OrganizationDetails[], undefined, AsyncThunkConfig>(
  'orgs/fetchOrgsForTable',
  async (_, thunkAPI) => {
    try {
      const params = thunkAPI.getState().orgsSlice.requestOrgsParams;
      const response = (await axios.post('v2_get_admin_organizations', params)) as OrganizationDetails[];
      return response ?? [];
    } catch (e) {
      logError(e, ['orgsSlice', 'fetchOrgsForTable']);
      return thunkAPI.rejectWithValue((e as AxiosError<ErrorMessage>).response?.data);
    }
  },
);

export const fetchOrgsForAdmin = createAsyncThunk<OrganizationDetails[], undefined, AsyncThunkConfig>(
  'orgs/fetchOrgsForAdmin',
  async (_, thunkAPI) => {
    try {
      const response = (await axios.post('v2_get_admin_organizations', {
        sortedInfo: new SortedInfo<OrganizationDetails>('name', 'ascend'),
      })) as OrganizationDetails[];
      return response ?? [];
    } catch (e) {
      logError(e, ['orgsSlice', 'fetchOrgsForAdmin']);
      return thunkAPI.rejectWithValue((e as AxiosError<ErrorMessage>).response?.data);
    }
  },
);

export const fetchOrgsForManager = createAsyncThunk<Organization[], undefined, AsyncThunkConfig>(
  'orgs/fetchOrgsForManager',
  async (_, thunkAPI) => {
    try {
      const response = (await axios.post('v2_get_organizations')) as Organization[];
      return response ?? [];
    } catch (e) {
      logError(e, ['orgsSlice', 'fetchOrgsForManager']);
      return thunkAPI.rejectWithValue((e as AxiosError<ErrorMessage>).response?.data);
    }
  },
);

export const addBQOrganization = createAsyncThunk<string | undefined, BQOrganization, AsyncThunkConfig>(
  'orgs/addBQOrganization',
  async (bqOrganization, thunkAPI) => {
    try {
      const response = (await axios.post('v0_add_bq_organizations', bqOrganization)) as string;
      if (response) {
        thunkAPI.dispatch(fetchOrgsForTable());
        thunkAPI.dispatch(setShowAddBQOrganizationModal(false));
        notification.success({
          title: 'BQ organization added successfully',
          description: 'Success',
        });
      }
      return response;
    } catch (e) {
      logError(e, ['orgsSlice', 'addBQOrganization']);
      return thunkAPI.rejectWithValue((e as AxiosError<ErrorMessage>).response?.data);
    }
  },
);

export const orgsSlice = createSlice({
  name: 'orgs',
  initialState,
  reducers: {
    handleOrgsTableChange: (state, action: PayloadAction<TableChange<OrganizationDetails>>) => {
      const filters = action.payload.filters as FilteredInfo;
      const sorter = action.payload.sorter as SortedInfo<OrganizationDetails>;
      state.requestOrgsParams.pagination = action.payload.pagination;
      state.requestOrgsParams.sortedInfo = sorter;
      state.requestOrgsParams.filteredInfo = removeEmpty(filters);
    },
    clearFilter: (state) => {
      state.requestOrgsParams.pagination = initialState.requestOrgsParams.pagination;
      state.requestOrgsParams.sortedInfo = initialState.requestOrgsParams.sortedInfo;
      state.requestOrgsParams.filteredInfo = initialState.requestOrgsParams.filteredInfo;
    },
    setShowAddBQOrganizationModal: (state, action: PayloadAction<boolean>) => {
      state.showAddBQOrganizationModal = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchOrgsForTable.pending, (state, _action) => {
        state.orgsApiStatus = APIStatus.PENDING;
      })
      .addCase(fetchOrgsForTable.fulfilled, (state, action) => {
        state.orgsForTable = action.payload;
        state.orgsApiStatus = APIStatus.FULFILLED;
      })
      .addCase(fetchOrgsForTable.rejected, (state, _action) => {
        state.orgsApiStatus = APIStatus.ERROR;
      })
      .addCase(fetchOrgsForAdmin.fulfilled, (state, action) => {
        const all = action.payload.map((o) => ({ ...o, label: `${o.name} (${o.app_code})` }));
        state.orgsForAdminDic.all = all;
        state.orgsForAdminDic.external = all.filter((o) => o.internal === false);
        state.orgsForAdminDic.ts = all.filter((o) => o.app_code === AppCode.TalkingStick);
        state.orgsForAdminDic.bq = all.filter((o) => o.app_code === AppCode.BuddysQuest);
        state.orgsForAdminDic.sp = all.filter((o) => o.use_service_providers);
        state.orgsForAdminDic.fa = all.filter((o) => o.use_first_alerts);
      })
      .addCase(fetchOrgsForManager.fulfilled, (state, action) => {
        state.orgsForManager = action.payload;
      });
  },
});

export const { handleOrgsTableChange, clearFilter, setShowAddBQOrganizationModal } = orgsSlice.actions;
